import React from 'react';
import dayjs from 'dayjs';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { IGetNotificationRes } from '@/types/api/notification/getNotification';
import { color } from '../../../styles';
import {
  MED_NOTIFICATION_TEXT_EN,
  MED_NOTIFICATION_TEXT_JA,
  NOTIFICATION_ICON_PATH,
  NOTIFICATION_TEXT_EN,
  NOTIFICATION_TEXT_JA,
} from '@/constants/notification';
import i18n from '@/i18n/configs';
import { showMedicalContentSelector } from '@/redux/config/selfData';

interface ResidentNotification extends IGetNotificationRes {
  residentName: string;
  residentLastName: string;
}

interface Props {
  notification: ResidentNotification;
}

const Container = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dddddd;
  padding: 8px 0;
  min-height: 60px;
  background: ${color.white};
`;

const MessageContainer = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const IconStyle = css`
  margin: 0 20px;
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NameContainer = css`
  font-size: 14px;
`;

const MessageBody = css`
  font-size: 12px;
  color: ${color.gray};
  white-space: pre-wrap;
`;

const DateContainer = css`
  font-family: 'Oswald', sans-serif;
  min-width: 120px;
  font-size: 12px;
  line-height: 18px;
  color: ${color.gray};
  text-wrap: nowrap;
  text-align: right;
  margin-right: 24px;
`;

const UrineVolumeContainer = css`
  min-width: 108px;
  text-align: left;
  font-size: 12px;
  font-weight: bold;
  color: #8ec2ec;
`;

export const NotificationListItem = React.memo(({ notification }: Props) => {
  let description = '';
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const shouldShowUrineVolume = showMedicalContent && typeof notification.urineVolume === 'number';

  const iconPath = NOTIFICATION_ICON_PATH[notification.notificationType] ?? NOTIFICATION_ICON_PATH.default;
  if (i18n.language.startsWith('ja')) {
    const textMap = showMedicalContent ? MED_NOTIFICATION_TEXT_JA : NOTIFICATION_TEXT_JA;
    description = textMap[notification.notificationType] ?? textMap.default;
  } else {
    const textMap = showMedicalContent ? MED_NOTIFICATION_TEXT_EN : NOTIFICATION_TEXT_EN;
    description = textMap[notification.notificationType] ?? textMap.default;
  }

  return (
    <div css={Container}>
      <img css={IconStyle} src={iconPath} />
      <div css={MessageContainer}>
        <div css={NameContainer}>
          {notification.residentLastName} {notification.residentName}
        </div>
        <div css={MessageBody}>{description}</div>
      </div>
      <div css={UrineVolumeContainer}>
        {shouldShowUrineVolume && <div>目安尿量: {notification.urineVolume}ml</div>}
      </div>
      <div css={DateContainer}>{dayjs(notification.timestamp).format('YYYY/MM/DD HH:mm')}</div>
    </div>
  );
});
